<template>
  <div class="settings-badges-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Badges</h5>
      <b-button class="pt-2 pb-2" size="sm" @click="addBadge" variant="primary"
        >New Badge</b-button
      >
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="badges"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      centered
      :title="form.id ? 'Edit Badge' : 'Add Badge'"
      v-model="showEditor"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitBadge"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group
        label="Name"
        :state="formErrors.name.length == 0 ? null : false"
        :invalid-feedback="formErrors.name.join(', ')"
      >
        <b-form-input
          v-model="form.name"
          maxLength="100"
          :state="formErrors.name.length == 0 ? null : false"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Acronym"
        :state="formErrors.acronym.length == 0 ? null : false"
        :invalid-feedback="formErrors.acronym.join(', ')"
      >
        <b-form-input
          v-model="form.acronym"
          maxLength="5"
          :state="formErrors.acronym.length == 0 ? null : false"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Category"
        :state="formErrors.category.length == 0 ? null : false"
        :invalid-feedback="formErrors.category.join(', ')"
      >
        <v-select :options="badgeCategories" v-model="form.category"></v-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Grid from "../../third_party/grid/Grid.vue";
import { validateRequired } from "../../helpers/validation";
import badgesServer from "../../server/badge";
import badgeCategoriesServer from "../../server/badgeCategory";

export default {
  name: "EocBadgeCrud",
  components: { Grid },
  data() {
    return {
      badges: [],
      badgeCategories: [],
      form: {
        id: undefined,
        name: "",
        acronym: "",
        category: {
          value: "",
          label: ""
        }
      },
      formErrors: {
        name: [],
        acronym: [],
        category: []
      },
      showEditor: false
    };
  },
  computed: {
    columns() {
      let columns = [];

      columns.push({
        text: "Name",
        field: "name",
        width: 250,
        editor: false
      });

      columns.push({
        text: "Category",
        field: "category.name",
        width: 250,
        editor: false,
        sortable(a, b) {
          return a.category.name.localeCompare(b.category.name);
        }
      });

      columns.push({
        text: "Acronym",
        field: "acronym",
        width: 250,
        editor: false
      });

      columns.push({
        text: "Actions",
        type: "action",
        cellCls: "pointer-cell",
        align: "right",
        width: 100,
        actions: [
          {
            tooltip: "Edit",
            renderer: () =>
              `<img class="b-action-item" src="${
                this.isStandalone()
                  ? `/icons/edit.svg`
                  : `/desktop/icons/edit.svg`
              }">`,
            onClick: ({ record }) => {
              this.showEditor = true;
              this.form = {
                id: record.data.id,
                name: record.data.name,
                acronym: record.data.acronym,
                category: {
                  label: record.data.category.name,
                  value: record.data.category.id
                }
              };
            }
          },
          {
            tooltip: "Delete",
            renderer: () =>
              `<img class="b-action-item ml-2" src="${
                this.isStandalone()
                  ? `/icons/delete.svg`
                  : `/desktop/icons/delete.svg`
              }">`,
            onClick: async ({ record }) => {
              await this.deleteBadge(record.data.id);
            }
          }
        ]
      });

      return columns;
    }
  },
  mounted() {
    this.getBadges();
    this.getBadgeCategories();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    async getBadges() {
      this.badges = await badgesServer.query();
    },
    async getBadgeCategories() {
      const categories = await badgeCategoriesServer.query();
      this.badgeCategories = categories.map(c => ({
        label: c.name,
        value: c.id
      }));
    },
    addBadge() {
      Object.assign(this.form, {
        id: undefined,
        name: "",
        acronym: "",
        category: ""
      });

      this.showEditor = true;
    },
    async submitBadge(modalEvent) {
      modalEvent.preventDefault();

      if (!this.validateForm()) {
        return;
      }

      const data = {
        name: this.form.name,
        acronym: this.form.acronym,
        categoryId: this.form.category.value
      };

      if (!this.form.id) {
        await badgesServer
          .create(data)
          .then(() => {
            this.showEditor = false;
            this.showAlertSuccess();
            this.getBadges();
          })
          .catch(e => {
            this.showError({
              message: e.response.data.errorCode
            });
          });
      } else {
        await badgesServer
          .update(this.form.id, data)
          .then(() => {
            this.showEditor = false;
            this.showAlertSuccess();
            this.getBadges();
          })
          .catch(e => {
            this.showError({
              message: e.response.data.errorCode
            });
          });
      }
    },
    async deleteBadge(id) {
      this.$dialog
        .confirm(
          {
            title: "Are you sure you want to delete this record?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await badgesServer.delete(id);
          this.showAlertSuccess();
          await this.getBadges();
        })
        .catch(function() {});
    },
    validateForm() {
      let withoutErrors = true;

      this.formErrors.name.splice(0);
      this.formErrors.acronym.splice(0);
      this.formErrors.category.splice(0);

      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      if (!validateRequired(this.form.acronym)) {
        this.formErrors.acronym.push("This field is required");
        withoutErrors = false;
      }

      if (this.form.acronym.length > 5) {
        this.formErrors.acronym.push(
          "The length of acronym must be 5 characters or fewer"
        );
        withoutErrors = false;
      }

      if (!this.form.category) {
        this.formErrors.category.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    }
  }
};
</script>

<style>
.settings-badges-container .b-grid-container .b-container {
  max-height: calc(100vh - 281px);
}
</style>
